import { useAtom, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import {
  earnDaysNavPermanentlyDismissedAtom,
  hasSeenTrialCompletionTooltipAtom,
  hasSeenTrialExtensionModalAtom,
  trialExtensionInviteModalOpenAtom,
  trialExtensionModalOpenAtom,
} from '@/atoms/freeTrialModalAtoms';
import {
  GAMIFY_DEV_STATES,
  type GamifyDevState,
  gamifyDevOverrideAtom,
} from '@/features/billing-dev/gamify-overrides';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { RadioGroup, RadioGroupItem } from '@heidi/design-system/radio-group';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyP2SemiBold,
} from '@heidi/design-system/typography';

interface GamifyTrialExtensionDevSectionProps {
  /** Called after an action that should close the dev-tools sheet. */
  onRequestClose?: () => void;
}

/**
 * Dev-tools section for the gamify trial extension: preview each checklist
 * state and reopen its modals and popups without completing real tasks.
 */
export const GamifyTrialExtensionDevSection = ({
  onRequestClose,
}: GamifyTrialExtensionDevSectionProps) => {
  const [override, setOverride] = useAtom(gamifyDevOverrideAtom);
  const setChecklistModalOpen = useSetAtom(trialExtensionModalOpenAtom);
  const setInviteModalOpen = useSetAtom(trialExtensionInviteModalOpenAtom);
  const setNavDismissed = useSetAtom(earnDaysNavPermanentlyDismissedAtom);
  const setHasSeenModal = useSetAtom(hasSeenTrialExtensionModalAtom);
  const setHasSeenCompletionTooltip = useSetAtom(hasSeenTrialCompletionTooltipAtom);

  const activeState = GAMIFY_DEV_STATES.find((state) => state.id === override);

  const handleStateChange = useCallback(
    (value: string) => {
      setOverride(value === 'none' ? null : (value as GamifyDevState));
    },
    [setOverride]
  );

  const handleOpenChecklist = useCallback(() => {
    setChecklistModalOpen(true);
    onRequestClose?.();
  }, [onRequestClose, setChecklistModalOpen]);

  const handleOpenInvite = useCallback(() => {
    setInviteModalOpen(true);
    onRequestClose?.();
  }, [onRequestClose, setInviteModalOpen]);

  const handleReplayCompletionTooltip = useCallback(() => {
    setOverride('earned');
    setNavDismissed(false);
    setHasSeenCompletionTooltip(false);
    onRequestClose?.();
  }, [onRequestClose, setHasSeenCompletionTooltip, setNavDismissed, setOverride]);

  const handleResetSeen = useCallback(() => {
    setNavDismissed(false);
    setHasSeenModal(false);
    setHasSeenCompletionTooltip(false);
  }, [setHasSeenCompletionTooltip, setHasSeenModal, setNavDismissed]);

  return (
    <section aria-labelledby="dev-tools-gamify-heading" className="mt-6">
      <TypographyP2SemiBold id="dev-tools-gamify-heading" className="mb-2">
        Gamify trial extension
      </TypographyP2SemiBold>
      <div className="flex flex-col gap-3">
        <TypographyCaption className="text-text-tertiary">
          Overrides the gamify progress on read, so the gift, hover card, checklist modal
          and completion tooltip show the chosen state. Per-tab; ignored in production.
          The gift still needs a trial the gate accepts: pair with the &quot;Heidi II
          Clinician trial&quot; billing preset above.
        </TypographyCaption>

        <div className="flex items-center justify-between gap-3">
          <TypographyCaption className="text-text-secondary">
            Active override
          </TypographyCaption>
          {activeState ? (
            <div className="flex items-center gap-2">
              <Badge size="xs" color="green">
                {activeState.label}
              </Badge>
              <Button variant="ghost" size="xxs" onClick={() => setOverride(null)}>
                Clear
              </Button>
            </div>
          ) : (
            <TypographyCaption className="text-text-tertiary">
              none — real progress
            </TypographyCaption>
          )}
        </div>

        <RadioGroup
          value={override ?? 'none'}
          onValueChange={handleStateChange}
          aria-label="Gamify trial extension states"
          className="gap-1.5"
        >
          <GamifyStateOption
            id="gamify-state-none"
            value="none"
            label="Real progress"
            description="No override — whatever the backend returns."
          />
          {GAMIFY_DEV_STATES.map((state) => (
            <GamifyStateOption
              key={state.id}
              id={`gamify-state-${state.id}`}
              value={state.id}
              label={state.label}
              description={state.description}
            />
          ))}
        </RadioGroup>

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="xs" onClick={handleOpenChecklist}>
            Open checklist modal
          </Button>
          <Button variant="secondary" size="xs" onClick={handleOpenInvite}>
            Open invite modal
          </Button>
          <Button variant="secondary" size="xs" onClick={handleReplayCompletionTooltip}>
            Replay completion tooltip
          </Button>
          <Button variant="ghost" size="xs" onClick={handleResetSeen}>
            Reset seen / dismissed
          </Button>
        </div>
      </div>
    </section>
  );
};

interface GamifyStateOptionProps {
  id: string;
  value: string;
  label: string;
  description: string;
}

const GamifyStateOption = ({ id, value, label, description }: GamifyStateOptionProps) => (
  <label
    htmlFor={id}
    className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border p-2.5 transition-colors hover:bg-surface-hover has-[[data-state=checked]]:border-border-selected has-[[data-state=checked]]:bg-surface-selected"
  >
    <RadioGroupItem id={id} value={value} className="mt-0.5 shrink-0" />
    <span className="flex min-w-0 flex-col gap-0.5">
      <TypographyCaptionBold>{label}</TypographyCaptionBold>
      <TypographyCaption className="whitespace-normal text-text-tertiary">
        {description}
      </TypographyCaption>
    </span>
  </label>
);
